/*
 *  ___                  __                __  _ _
 *   | |_  _     _ |_ |_|_    _  _ _ _ _  /  \(_(_|. _  _
 *   | | )(_)|_|(_)| )|_|__)(|_)| (-_)_)  \__/| | ||| )(-
 *              _/           |
 */

/* Four functions are expected to implement a process
 *
 * 1. createState : state -> ()
 *    - create the currentProcess property of the state
 *
 * 2. processButton : (processState, { id }) -> ()
 *    - called when a button is clicked to modify the process state
 *
 * 3. processChange : (processState, { id, uid, value }) => ()
 *    - called when an input value is changes to modify the process state
 *
 * 4. processItemClick : (processState, { id, uid }) => ()
 *    - called when an item is clicked to modify the process state
 */

import {
  pages,
  familyMemberLabels,
  familyMemberOptions,
  additionalLife,
  createFamilyMember,
  familyMemberTablePrefix,
  familyMemberItem,
  buttonFromLifeAssured,
  idFromLifeAssured,
  paymentDetailsSummary,
  livesAssuredItem,
} from './family-support-plan/pages'
import shortid from 'shortid'
import {
  updateItemIn,
  createButton,
  slugify,
  createItem,
  findItemsIn,
} from '../../../src/core/dataitems'
import { form } from './family-support-plan/form'

import { cancelProcess, completeProcess } from '../../../src/core/actions'
import dayjs from 'dayjs'
import { createSelector } from 'reselect'
import { original } from 'immer'
import { validate } from './family-support-plan/validations'

const dateFormat = 'D MMMM YYYY'

const amountString = (f) =>
  f
    .toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/,/, '')

const mainLifeAge = createSelector(
  (form) => form['life-assured-identification']['date-of-birth'],
  (dob) => dayjs().diff(dob, 'year')
)

const productOption = createSelector(
  (form) => form['cover-options']['gmd5000'],
  (form) => form['cover-options']['gmd10000'],
  (form) => form['cover-options']['gmd15000'],
  (form) => form['cover-options']['gmd20000'],
  (form) => form['cover-options']['gmd30000'],
  (form) => form['cover-options']['gmd40000'],
  (form) => form['cover-options']['gmd50000'],
  (five, ten, fifteen, twenty, thirty, forty, fifty) => {
    if (five === 'Y') {
      return 1
    } else if (ten === 'Y') {
      return 2
    } else if (fifteen === 'Y') {
      return 3
    } else if (twenty === 'Y') {
      return 4
    } else if (thirty === 'Y') {
      return 5
    } else if (forty === 'Y') {
      return 6
    } else if (fifty === 'Y') {
      return 7
    }
    return 0
  }
)

const benefitRate = createSelector(productOption, (coveroption) => {
  if (coveroption === 1) {
    return 5000
  } else if (coveroption === 2) {
    return 10000
  } else if (coveroption === 3) {
    return 15000
  } else if (coveroption === 4) {
    return 20000
  } else if (coveroption === 5) {
    return 30000
  } else if (coveroption === 6) {
    return 40000
  } else if (coveroption === 7) {
    return 50000
  }
})

//RATES
// Basic Benefit Table
const BASIC_BENEFIT_RATES = [
  // 5000, 10000, 15000, 20000, 30000, 40000, 50000
  [162.15, 165.21, 168.39, 171.59, 177.85, 184.28, 190.74], // Principal
  [191.19, 223.22, 255.46, 287.65, 352.0, 416.41, 481.05], // Principal + 1 Extended
  [220.22, 281.24, 342.53, 403.72, 526.15, 648.54, 771.37], // Principal + 2 Extended
  [249.25, 339.25, 429.61, 519.78, 700.3, 880.67, 1061.68], // Principal + 3 Extended
  [278.29, 397.27, 516.68, 635.85, 874.45, 1112.8, 1352.0], // Principal + 4 Extended
  [307.32, 455.29, 603.76, 751.91, 1048.6, 1344.92, 1642.31], // Principal + 5 Extended
  [336.36, 513.3, 690.83, 867.98, 1222.75, 1577.05, 1932.63], // Principal + 6 Extended
  [365.39, 571.32, 777.91, 984.04, 1396.9, 1809.18, 2222.95], // Principal + 7 Extended
  [394.42, 629.33, 864.98, 1100.11, 1571.05, 2041.31, 2513.26], // Principal + 8 Extended
  [423.46, 687.35, 952.06, 1216.17, 1745.2, 2273.44, 2803.58], // Principal + 9 Extended
  [452.49, 745.36, 1039.13, 1332.23, 1919.35, 2505.57, 3093.89], // Principal + 10 Extended
  [481.53, 803.38, 1126.21, 1448.3, 2093.5, 2737.7, 3384.21], // Principal + 11 Extended
  [510.56, 861.39, 1213.28, 1564.36, 2267.65, 2969.83, 3674.53], // Principal + 12 Extended
  [539.6, 919.41, 1300.36, 1680.43, 2441.8, 3201.96, 3964.84], // Principal + 13 Extended
  [568.63, 977.43, 1387.43, 1796.49, 2615.95, 3434.09, 4255.16], // Principal + 14 Extended
]
// One Year Memorial Option Table
const MEMORIAL_OPTION_RATES = [
  // 2500, 5000, 7500, 10000, 15000, 20000, 25000
  [1.68, 3.36, 5.04, 6.72, 10.08, 13.44, 16.81], // Principal
  [21.45, 42.84, 64.31, 85.72, 128.63, 171.44, 214.44], // Principal + 1 Extended
  [41.21, 82.33, 123.59, 164.72, 247.18, 329.44, 412.08], // Principal + 2 Extended
  [60.98, 121.81, 182.86, 243.72, 365.72, 487.44, 609.71], // Principal + 3 Extended
  [80.74, 161.3, 242.13, 322.72, 484.27, 645.44, 807.34], // Principal + 4 Extended
  [100.51, 200.79, 301.41, 401.72, 602.81, 803.45, 1004.97], // Principal + 5 Extended
  [120.27, 240.27, 360.68, 480.72, 721.36, 961.45, 1202.6], // Principal + 6 Extended
  [140.04, 279.76, 420.45, 559.72, 839.9, 1119.45, 1400.24], // Principal + 7 Extended
  [159.8, 319.24, 479.22, 638.72, 958.45, 1277.45, 1597.87], // Principal + 8 Extended
  [179.57, 358.73, 538.5, 717.72, 1077.0, 1435.45, 1795.5], // Principal + 9 Extended
  [199.33, 398.21, 597.77, 796.73, 1195.54, 1593.45, 1993.13], // Principal + 10 Extended
  [219.09, 437.7, 657.04, 875.73, 1314.09, 1751.45, 2190.76], // Principal + 11 Extended
  [238.86, 477.18, 716.31, 954.73, 1432.63, 1909.45, 2388.4], // Principal + 12 Extended
  [258.62, 516.67, 775.59, 1033.73, 1551.18, 2067.45, 2586.03], // Principal + 13 Extended
  [278.39, 556.15, 834.86, 1112.73, 1669.72, 2225.45, 2783.66], // Principal + 14 Extended
]
const POLICY_FEE = 6.0

function getCoverIndex(coverOption) {
  return coverOption - 1
}
function getLivesIndex(livesAssured) {
  return Math.min((livesAssured || []).length, 14)
}

// Main premium calculation
const mainLifePremium = createSelector(
  (form) => form['cover-options'],
  (form) => form['lives-assured'],
  productOption,
  (coverOptions, livesAssured, coverOption) => {
    if (!coverOption) return 0
    const coverIdx = getCoverIndex(coverOption)
    const livesIdx = getLivesIndex(livesAssured)
    return BASIC_BENEFIT_RATES[livesIdx][coverIdx]
  }
)

// One year memorial option premium calculation
const memorialPremium = createSelector(
  (form) => form['one-year-memorial'],
  (form) => form['cover-options'],
  (form) => form['lives-assured'],
  productOption,
  (memorial, coverOptions, livesAssured, coverOption) => {
    if (memorial !== 'Y' || !coverOption) return 0
    const coverIdx = getCoverIndex(coverOption)
    const livesIdx = getLivesIndex(livesAssured)
    return MEMORIAL_OPTION_RATES[livesIdx][coverIdx]
  }
)

// Get the max cash bonus (basic benefit rate)
function getMaxCashBonus(livesAssured, coverOption) {
  if (!coverOption) return 0
  const coverIdx = getCoverIndex(coverOption)
  const livesIdx = getLivesIndex(livesAssured)
  return BASIC_BENEFIT_RATES[livesIdx][coverIdx]
}

// Cash bonus premium calculation
const cashBonusPremium = createSelector(
  (form) => form['cash-bonus'],
  (form) => form['lives-assured'],
  productOption,
  (cashBonus, livesAssured, coverOption) => {
    if (!coverOption) return 0
    let value = Number(cashBonus)
    if (isNaN(value)) return 0
    const min = 70
    const max = getMaxCashBonus(livesAssured, coverOption)
    if (value < min) return 0
    if (value > max) value = max
    return value
  }
)

// Update totalQuotedPremium to include cash bonus
const totalQuotedPremium = createSelector(
  mainLifePremium,
  memorialPremium,
  cashBonusPremium,
  (main, memorial, cashBonus) => {
    let premium = main + memorial + cashBonus + POLICY_FEE
    return premium
  }
)

const paymentFrequency = createSelector(
  (form) => form['payment-frequency'],
  (freq) => {
    if (!freq) return 'monthly'
    if (freq['monthly'] === 'Y') return 'monthly'
    if (freq['quarterly'] === 'Y') return 'quarterly'
    if (freq['yearly'] === 'Y') return 'yearly'
    return 'monthly'
  }
)

const displayPremium = createSelector(
  totalQuotedPremium,
  paymentFrequency,
  (monthlyPremium, freq) => {
    const POLICY_FEE = 6.0
    switch (freq) {
      case 'quarterly':
        return monthlyPremium * 3
      case 'yearly': {
        const baseMonthly = monthlyPremium - POLICY_FEE
        const annualBase = baseMonthly * 12
        const discounted = annualBase * 0.9
        return discounted + 72.0
      }
      case 'monthly':
      default:
        return monthlyPremium
    }
  }
)

const startOfNextMonths = (monthCount) => {
  const result = []
  const now = dayjs()
  for (let m = 1; m <= monthCount; m++) {
    result.push(now.add(m, 'month').startOf('month').format(dateFormat))
  }
  return result
}

const optionsForRelationship = (opts, relationship) => {
  if (relationship in opts) {
    return opts[relationship].options || []
  } else {
    const alt = Object.values(opts).find((v) => v.rename === relationship)
    return alt ? alt.options || [] : null
  }
}

// Prodct rules say spouse gender must be opposite to main life gender :(
const spouseGender = (gender) => {
  switch (gender) {
    case 'Male':
      return 'Female'
    case 'Female':
      return 'Male'
    default:
      return null
  }
}

const hasSpouse = (state) =>
  state.form['lives-assured'].find((l) => l.relationship === 'Spouse')
const hasMother = (state) =>
  state.form['lives-assured'].find((l) => l.relationship === 'Mother')
const hasFather = (state) =>
  state.form['lives-assured'].find((l) => l.relationship === 'Father')
const hasMotherInLaw = (state) =>
  state.form['lives-assured'].find((l) => l.relationship === 'Mother in law')
const hasFatherInLaw = (state) =>
  state.form['lives-assured'].find((l) => l.relationship === 'Father in law')

const quoteCanBeCalculated = (state) =>
  state.form['life-assured-identification']['date-of-birth'] &&
  productOption(state.form) > 0

const setPageItemFromState = (state) => {
  validate(state, state.local && state.local.failed_validation ? false : true)

  switch (state.step) {
    case 'quotation-screen':
      // Date of birth
      updateItemIn(
        state.page.item,
        { id: 'date-of-birth' },
        {
          content:
            state.form['life-assured-identification']['date-of-birth'] || '',
          maxYear: dayjs().year() - 18,
          minYear: dayjs().year() - 100,
        }
      )
      //gender
      updateItemIn(
        state.page.item,
        { id: 'gender' },
        {
          content: state.form['life-assured-identification']['gender'] || '',
        }
      )

      // Cover options
      updateItemIn(
        state.page.item,
        { id: 'gmd5000', type: 'field' },
        {
          content: state.form['cover-options']['gmd5000'] || '',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'gmd10000', type: 'field' },
        {
          content: state.form['cover-options']['gmd10000'] || '',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'gmd15000', type: 'field' },
        {
          content: state.form['cover-options']['gmd15000'] || '',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'gmd20000', type: 'field' },
        {
          content: state.form['cover-options']['gmd20000'] || '',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'gmd30000', type: 'field' },
        {
          content: state.form['cover-options']['gmd30000'] || '',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'gmd40000', type: 'field' },
        {
          content: state.form['cover-options']['gmd40000'] || '',
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'gmd50000', type: 'field' },
        {
          content: state.form['cover-options']['gmd50000'] || '',
        }
      )

      //Hide family member option if no cover has been selected
      updateItemIn(
        state.page.item,
        { id: 'your-family', type: 'list' },
        {
          invisible: !(
            state.form['cover-options']['gmd5000'] === 'Y' ||
            state.form['cover-options']['gmd10000'] === 'Y' ||
            state.form['cover-options']['gmd15000'] === 'Y' ||
            state.form['cover-options']['gmd20000'] === 'Y' ||
            state.form['cover-options']['gmd30000'] === 'Y' ||
            state.form['cover-options']['gmd40000'] === 'Y' ||
            state.form['cover-options']['gmd50000'] === 'Y'
          ),
        }
      )

      //Added Lives insured
      updateItemIn(
        state.page.item,
        { id: 'lives-added-basic' },
        {
          invisible: state.form['lives-assured'].length === 0,
          content: state.form['lives-assured'].map(additionalLife),
        }
      )

      //Hide button to add additional lives - max 14
      const numLives = (state.form['lives-assured'] || []).length
      updateItemIn(
        state.page.item,
        { id: 'system-constant', uid: 'e9bba8eeff937b49' },
        { invisible: numLives >= 14 }
      )

      // Memorial option
      updateItemIn(
        state.page.item,
        { id: 'one-year-memorial', type: 'field' },
        {
          content: state.form['one-year-memorial'] || '',
        }
      )

      // Cash bonus
      updateItemIn(
        state.page.item,
        { id: 'cash-bonus', type: 'real' },
        {
          content: state.form['cash-bonus'] || '',
        }
      )

      // Inflation protector
      updateItemIn(
        state.page.item,
        { id: 'inflation-protector', type: 'field' },
        {
          content: state.form['inflation-protector'] || '',
        }
      )

      break

    case 'add-family-members':
      let familyButtonLabels = familyMemberLabels
      if ((state.form['lives-assured'] || []).length >= 14) {
        // Prevent adding more than 14 additional lives
        familyButtonLabels = []
      } else {
        // Hide spouse button if there already is a spouse
        if (hasSpouse(state)) {
          familyButtonLabels = familyButtonLabels.filter((b) => b !== 'Spouse')
        }
        // Hide mother button if there already is a mother
        if (hasMother(state)) {
          familyButtonLabels = familyButtonLabels.filter((b) => b !== 'Mother')
        }
        // Hide father button if there already is a father
        if (hasFather(state)) {
          familyButtonLabels = familyButtonLabels.filter((b) => b !== 'Father')
        }
        // Hide mother in law button if there already is a mother in law
        if (hasMotherInLaw(state)) {
          familyButtonLabels = familyButtonLabels.filter(
            (b) => b !== 'Mother in law'
          )
        }
        // Hide father in law button if there already is a father in law
        if (hasFatherInLaw(state)) {
          familyButtonLabels = familyButtonLabels.filter(
            (b) => b !== 'Father in law'
          )
        }
      }
      state.page.buttons = familyButtonLabels.map(createButton)
      state.page.buttons.push(createButton('Back', { control: true }))
      break

    case 'family-members-age-and-gender':
      state.page.text = [
        `Please provide the age and gender of your ${
          state.local.relationship || 'family member'
        }`,
      ]

      updateItemIn(
        state.page.item,
        { id: 'age-of-member' },
        {
          content: state.local['age-of-member'] || '',
          options: state.local.options || [],
        }
      )

      updateItemIn(
        state.page.item,
        { id: 'gender' },
        { content: state.local.gender || '' }
      )
      break

    case 'edit-family-members':
      state.page.buttons = state.form['lives-assured'].map(
        buttonFromLifeAssured
      )
      state.page.buttons.push(createButton('Back', { control: true }))
      break

    case 'remove-family-members':
      state.page.buttons = state.form['lives-assured'].map(
        buttonFromLifeAssured
      )
      state.page.buttons.push(createButton('Back', { control: true }))
      break

    case 'personal-details': {
      Object.entries(state.form['life-assured-identification'] || {}).forEach(
        ([key, value]) => {
          if (key === 'id-type-radio-group') {
            Object.entries(value || {}).forEach(([subKey, subValue]) => {
              updateItemIn(
                state.page.item,
                { id: subKey },
                { content: subValue || '' }
              )
            })
          } else {
            updateItemIn(state.page.item, { id: key }, { content: value || '' })
          }
        }
      )
      // Contact details
      Object.entries(state.form['life-assured-contact-details'] || {}).forEach(
        ([key, value]) => {
          updateItemIn(state.page.item, { id: key }, { content: value || '' })
        }
      )
      // Postal address (nested list)
      Object.entries(state.form['life-assured-postal-address'] || {}).forEach(
        ([key, value]) => {
          updateItemIn(state.page.item, { id: key }, { content: value || '' })
        }
      )
      // Employment details (if present)
      if (state.form['life-assured-employment-details']) {
        Object.entries(state.form['life-assured-employment-details']).forEach(
          ([key, value]) => {
            updateItemIn(state.page.item, { id: key }, { content: value || '' })
          }
        )
      }
      break
    }

    case 'trustee-details':
      Object.entries(state.form['trustee-identification'] || {}).forEach(
        ([key, value]) => {
          updateItemIn(state.page.item, { id: key }, { content: value || '' })
        }
      )
      break

    case 'additional-lives':
      updateItemIn(
        state.page.item,
        { id: 'additional-lives' },
        {
          content: familyMemberTablePrefix.concat(
            state.form['lives-assured'].map(familyMemberItem)
          ),
        }
      )
      validate(
        state,
        state.local && state.local.failed_validation ? false : true
      )
      break

    case 'payment-details':
      updateItemIn(
        state.page.item,
        { id: 'payment-frequency' },
        {
          content: state.form['payment-frequency'] || {},
        }
      )
      updateItemIn(
        state.page.item,
        { id: 'debit-order' },
        { content: state.form['debit-order'] || '' }
      )
      updateItemIn(
        state.page.item,
        { id: 'stop-order' },
        { content: state.form['stop-order'] || '' }
      )
      updateItemIn(
        state.page.item,
        { id: 'mobile-wallet' },
        { content: state.form['mobile-wallet'] || '' }
      )

      const months = startOfNextMonths(3)
      if (!state.form['payment-start-date']) {
        state.form['payment-start-date'] = dayjs(months[0]).format('YYYYMMDD')
      }
      updateItemIn(
        state.page.item,
        { id: 'payment-start-date' },
        {
          content: dayjs(state.form['payment-start-date']).format(dateFormat),
          options: months,
        }
      )

      updateItemIn(
        state.page.item,
        { uid: 'ce35234c9c489acc' },
        {
          invisible:
            state.form['life-assured-identification']['marital-status'] !==
            'Married',
        }
      )
      break

    case 'debit-order-details':
      Object.keys(state.form['payment-bank-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          {
            content: state.form['payment-bank-details'][field] || '',
          }
        )
      )
      break
    case 'stop-order-details':
      Object.keys(state.form['payment-employment-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          {
            content: state.form['payment-employment-details'][field] || '',
          }
        )
      )
      break

    case 'mobile-wallet-details':
      Object.keys(state.form['mobile-wallet-details']).forEach((field) =>
        updateItemIn(
          state.page.item,
          { id: field },
          {
            content: state.form['mobile-wallet-details'][field] || '',
          }
        )
      )
      break

    case 'upload-document-pages':
      state.local.pages.forEach((doc, idx) =>
        updateItemIn(
          state.page.item,
          { id: `upload-${idx + 1}` },
          { content: doc }
        )
      )
      break

    case 'acceptance-screen':
      Object.keys(state.form).forEach((section) => {
        findItemsIn(state.page.item, { id: section }).forEach((sItem) => {
          Object.keys(state.form[section]).forEach((field) => {
            if (state.form[section][field]) {
              updateItemIn(
                sItem,
                { id: field },
                { content: state.form[section][field] }
              )
            }
          })
        })
      })
      // Update the reference number
      updateItemIn(
        state.page.item,
        { id: 'contract-id' },
        { content: state.form['reference-number'] }
      )
      // Populate the lives-assured table in the summary section
      updateItemIn(
        state.page.item,
        { id: 'lives-assured', type: 'table' },
        {
          content: (state.form['lives-assured'] || []).map(livesAssuredItem),
        }
      )
      break
  }

  updateItemIn(
    state.page.item,
    { id: 'benefit-rate' },
    {
      content: quoteCanBeCalculated(state) ? benefitRate(state.form) : '0.00',
    }
  )

  updateItemIn(
    state.page.item,
    { id: 'premium' },
    {
      content: quoteCanBeCalculated(state)
        ? amountString(displayPremium(state.form))
        : '0.00',
    }
  )
}

// Change the page
const changePage = (state, page) => {
  if (page in pages) {
    state.page = pages[page]
    state.step = page
    state.local = null
  }
}

// Create the state for a new process
const createState = (state) => {
  state.currentProcess = {} // Create a new process
  state.currentProcess.form = Object.assign(
    { 'reference-number': shortid.generate() },
    form
  ) // Overall Process state
  state.currentProcess.step = 'quotation-screen' // Current process step
  state.currentProcess.page = pages['quotation-screen'] // Current process page data item
  state.currentProcess.local = null // Current process page local state
  setPageItemFromState(state.currentProcess) // Update initial page
}

// processButton
const processButton = (state, button, asyncDispatch) => {
  if (button.id === 'cancel') {
    const step = state.step
    changePage(state, 'confirm-cancel')
    state.local = state.local || {}
    state.local.previous = step
  } else {
    switch (state.step) {
      case 'confirm-cancel':
        switch (button.id) {
          case 'yes':
            asyncDispatch(cancelProcess(state['process-id']))
            break
          case 'no':
            changePage(state, state.local.previous)
            break
        }
        break
      case 'quotation-screen':
        switch (button.id) {
          case 'next':
            if (validate(state)) {
              if (hasSpouse(state)) {
                state.form['life-assured-identification']['marital-status'] =
                  'Married'
                if (state.form['life-assured-identification'].gender !== null) {
                  state.form['spouse-identification'].gender = spouseGender(
                    state.form['life-assured-identification'].gender
                  )
                }
              }
              // Store the initial gender for later validation
              state.form['initial-gender'] =
                state.form['life-assured-identification'].gender
              changePage(state, 'personal-details')
            }
            break
          case 'add-family':
            changePage(state, 'add-family-members')
            break
          case 'edit-family':
            const locals = state.local
            changePage(state, 'edit-family-members')
            state.local = locals
            break
          case 'remove-family':
            changePage(state, 'remove-family-members')
            break
        }
        break
      case 'add-family-members':
        if (button.id === 'back') {
          changePage(state, 'quotation-screen')
        } else {
          let optionLevel = familyMemberOptions(mainLifeAge(state.form) || 0)
          if (state.local && state.local.group) {
            optionLevel = optionLevel[state.local.group]
          }
          const elem = Object.keys(optionLevel).find(
            (l) => button.id === slugify(l)
          )
          if (elem) {
            const options = optionLevel[elem]
            if (options && options.options) {
              changePage(state, 'family-members-age-and-gender')
              state.local = state.local || {}
              state.local.options = options.options
              state.local.relationship = options.rename || elem
              delete state.local.group
            } else if (typeof options === 'object') {
              state.local = state.local || {}
              state.local.group = elem
              // Filter out unique parent types if already present
              let subKeys = Object.keys(options)
              if (elem === 'Parent or In-Laws') {
                if (hasMother(state)) {
                  subKeys = subKeys.filter((k) => k !== 'Mother')
                }
                if (hasFather(state)) {
                  subKeys = subKeys.filter((k) => k !== 'Father')
                }
                if (hasMotherInLaw(state)) {
                  subKeys = subKeys.filter((k) => k !== 'Mother in law')
                }
                if (hasFatherInLaw(state)) {
                  subKeys = subKeys.filter((k) => k !== 'Father in law')
                }
              }
              state.page.buttons = subKeys.map((sub) => createButton(sub))
              state.page.buttons.push(createButton('Back', { control: true }))
              return
            } else {
              state.local = state.local || {}
              state.local.relationship = elem
              delete state.local.group
            }
          }
        }
        break
      case 'family-members-age-and-gender':
        switch (button.id) {
          case 'back':
            changePage(state, 'add-family-members')
            break
          case 'next':
            if (validate(state)) {
              if (
                state.local &&
                ['relationship', 'age-of-member', 'gender'].every(
                  (f) => f in state.local
                )
              ) {
                const life = createFamilyMember(
                  state.local.relationship,
                  state.local['age-of-member'],
                  state.local.gender
                )
                if (
                  (state.local.replace || state.local.replace === 0) &&
                  state.local.replace >= 0 &&
                  state.local.replace < state.form['lives-assured'].length
                ) {
                  state.form['lives-assured'][state.local.replace] = life
                } else {
                  state.form['lives-assured'].push(life)
                }
                changePage(state, 'quotation-screen')
              }
            }
            break
        }
        break

      case 'edit-family-members':
        if (button.id === 'back') {
          changePage(state, 'quotation-screen')
        } else {
          const lifePosition = state.form['lives-assured'].findIndex(
            (life, position, lives) =>
              idFromLifeAssured(life, position, lives) === button.id
          )
          if (lifePosition >= 0) {
            changePage(state, 'family-members-age-and-gender')
            const life = original(state.form['lives-assured'][lifePosition])
            state.local = {
              replace: lifePosition,
              relationship: life.relationship,
              'age-of-member': life['age-of-member'],
              gender: life.gender,
            }
            const optionLevel =
              familyMemberOptions(mainLifeAge(state.form) || 0) || []
            state.local.options = optionsForRelationship(
              optionLevel,
              life.relationship
            )
            if (state.local.options === null) {
              state.local.options = Object.keys(optionLevel).reduce(
                (options, key) =>
                  optionsForRelationship(optionLevel[key], life.relationship) ||
                  options,
                []
              )
            }
          }
        }
        break
      case 'remove-family-members':
        state.form['lives-assured'] = state.form['lives-assured'].filter(
          (life, position, lives) =>
            idFromLifeAssured(life, position, lives) !== button.id
        )
        changePage(state, 'quotation-screen')
        break
      case 'personal-details':
        switch (button.id) {
          case 'back':
            changePage(state, 'quotation-screen')
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'trustee-details')
            }
            break
        }
        break

      case 'trustee-details':
        switch (button.id) {
          case 'back':
            changePage(state, 'personal-details')
            break
          case 'next':
            if (validate(state)) {
              changePage(
                state,
                state.form['lives-assured'].length > 0
                  ? 'additional-lives'
                  : 'payment-details',
                (state.form['payment-frequency']['monthly'] = 'Y')
              )
            }
            break
        }
        break

      case 'additional-lives':
        switch (button.id) {
          case 'back':
            changePage(state, 'trustee-details')
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'payment-details')
            }
            break
        }
        break

      case 'payment-details':
        switch (button.id) {
          case 'back':
            changePage(
              state,
              state.form['lives-assured'].length > 0
                ? 'additional-lives'
                : 'trustee-details'
            )
            break
          case 'next':
            if (validate(state)) {
              if (state.form['debit-order'] === 'Y') {
                changePage(state, 'debit-order-details')
              } else if (state.form['stop-order'] === 'Y') {
                changePage(state, 'stop-order-details')
              } else if (state.form['mobile-wallet'] === 'Y') {
                changePage(state, 'mobile-wallet-details')
              }
            }
            break
        }
        break

      case 'debit-order-details':
      case 'stop-order-details':
      case 'mobile-wallet-details':
        switch (button.id) {
          case 'back':
            changePage(state, 'payment-details')
            state.form['payer-signature'] = null
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'upload-documents')
            }
            break
        }
        break

      case 'upload-documents':
        switch (button.id) {
          case 'upload-id-document':
            changePage(state, 'upload-document-pages')
            state.local = state.local || {}
            state.local.document = button.id.substring(7)
            state.local.pages =
              original(state.form.documents[state.local.document]) || []
            const docs =
              state.local.pages.length === 0 ? [null] : state.local.pages
            updateItemIn(
              state.page.item,
              { id: 'uploads' },
              {
                content: docs.map((doc, idx) => {
                  return [
                    createItem('Upload', {
                      component: 'ImageDrop',
                      id: `upload-${idx + 1}`,
                      content: doc,
                      readonly: false,
                      type: 'component',
                    }),
                  ]
                }),
              }
            )
            break
          case 'next':
            if (validate(state)) {
              changePage(state, 'acceptance-screen')
              updateItemIn(
                state.page.item,
                { uid: 'd2994131363640e0', type: 'list' }, // payment-details
                paymentDetailsSummary(state.form)
              )
            }
            break
          case 'back':
            if (state.form['debit-order'] === 'Y') {
              changePage(state, 'debit-order-details')
            } else if (state.form['stop-order'] === 'Y') {
              changePage(state, 'stop-order-details')
            } else if (state.form['mobile-wallet'] === 'Y') {
              changePage(state, 'mobile-wallet-details')
            }
            state.form['payer-signature'] = null
            break
        }
        break

      case 'upload-document-pages':
        switch (button.id) {
          case 'continue':
            state.form.documents[state.local.document] = state.local.pages || []
            if (state.form.documents[state.local.document].length > 0) {
              changePage(state, 'acceptance-screen')
              updateItemIn(
                state.page.item,
                { uid: 'd2994131363640e0', type: 'list' }, // payment-details
                paymentDetailsSummary(state.form)
              )
            } else {
              changePage(state, 'upload-documents')
            }
            break
          case 'abort':
            changePage(state, 'upload-documents')
            break
          case 'add-page':
            const uploadsItem = findItemsIn(state.page.item, { id: 'uploads' })
            if (uploadsItem.length > 0) {
              const currentContent = original(uploadsItem[0].content)
              updateItemIn(
                state.page.item,
                { id: 'uploads' },
                {
                  content: currentContent.concat([
                    [
                      createItem('Upload', {
                        component: 'ImageDrop',
                        id: `upload-${currentContent.length + 1}`,
                        content: null,
                        readonly: false,
                        type: 'component',
                      }),
                    ],
                  ]),
                }
              )
            }
            break
        }
        break

      case 'acceptance-screen':
        switch (button.id) {
          case 'submit':
            if (validate(state)) {
              state.form['application-accepted'] = 'Y'
              state.form['application-acceptance-date'] =
                dayjs().format('YYYYMMDD')
              state.form.premium = amountString(totalQuotedPremium(state.form))
              changePage(state, 'completed')
            }
            break
          case 'back':
            changePage(state, 'upload-documents')
            break
        }
        break
      case 'completed':
        asyncDispatch(completeProcess(state.form['reference-number']))
        break
    }
    setPageItemFromState(state)
  }
}

// processChange
const processChange = (state, data) => {
  let dirty = false
  switch (state.step) {
    case 'quotation-screen':
      if (data.id in state.form['life-assured-identification']) {
        state.form['life-assured-identification'][data.id] = data.value
        dirty = true
      } else if (data.id in state.form['cover-options']) {
        // Set all cover-options to null, then set selected to 'Y'
        Object.keys(state.form['cover-options']).forEach((k) => {
          state.form['cover-options'][k] = null
        })
        state.form['cover-options'][data.id] = 'Y'
        dirty = true
      } else if (data.id === 'one-year-memorial') {
        state.form['one-year-memorial'] = data.value
        dirty = true
      } else if (data.id === 'cash-bonus') {
        state.form['cash-bonus'] = data.value
        dirty = true
      } else if (data.id === 'inflation-protector') {
        state.form['inflation-protector'] = data.value
        dirty = true
      } else {
        console.warn(`No form element found for ${data.id} [${data.value}]`)
      }
      if (data.id === 'date-of-birth') {
        state.form['main-cover'] = 'Main'
        dirty = true
      }
      break
    case 'family-members-age-and-gender':
      state.local = state.local || {}
      state.local[data.id] = data.value
      dirty = true
      break
    case 'personal-details':
      if (data.id in state.form['life-assured-identification']) {
        state.form['life-assured-identification'][data.id] = data.value
        dirty = true
      } else if (
        state.form['life-assured-identification']['id-type-radio-group'] &&
        data.id in
          state.form['life-assured-identification']['id-type-radio-group']
      ) {
        // Set all id-type-radio-group options to null, then set selected to 'Y'
        Object.keys(
          state.form['life-assured-identification']['id-type-radio-group']
        ).forEach((k) => {
          state.form['life-assured-identification']['id-type-radio-group'][k] =
            null
        })
        state.form['life-assured-identification']['id-type-radio-group'][
          data.id
        ] = 'Y'
        dirty = true
      } else if (
        state.form['life-assured-identification']['id-type-radio-group'] &&
        data.id in
          state.form['life-assured-identification']['id-type-radio-group']
      ) {
        state.form['life-assured-identification']['id-type-radio-group'][
          data.id
        ] = data.value
        dirty = true
      } else if (data.id in state.form['life-assured-contact-details']) {
        state.form['life-assured-contact-details'][data.id] = data.value
        dirty = true
      } else if (data.id in state.form['life-assured-postal-address']) {
        state.form['life-assured-postal-address'][data.id] = data.value
        dirty = true
      } else {
        console.warn(`No form element found for ${data.id} [${data.value}]`)
      }

      if (data.id === 'marital-status') {
        state.form['spouse-identification'].gender =
          data.value === 'Married'
            ? spouseGender(state.form['life-assured-identification'].gender)
            : null
        state.form['spouse-identification']['marital-status'] =
          data.value === 'Married' ? 'Married' : null
        dirty = true
      } else if (data.id === 'gender') {
        if (
          state.form['life-assured-identification']['marital-status'] ===
          'Married'
        ) {
          state.form['spouse-identification'].gender = spouseGender(data.value)
          state.form['spouse-identification']['marital-status'] = 'Married'
          dirty = true
        }
      }
      break

    case 'trustee-details':
      if (data.id in state.form['trustee-identification']) {
        state.form['trustee-identification'][data.id] = data.value
        dirty = true
      } else {
        console.warn(`No form element found for ${data.id} [${data.value}]`)
      }
      break

    case 'additional-lives':
      let id = data.id.split('-')
      const index = parseInt(id.pop())
      if (!isNaN(index) && index < state.form['lives-assured'].length) {
        id = id.join('-')
        if (id in state.form['lives-assured'][index]) {
          state.form['lives-assured'][index][id] = data.value
          dirty = true
        } else {
          console.warn(`No form element found for ${data.id} [${data.value}]`)
        }
      }
      break

    case 'payment-details':
      switch (data.id) {
        case 'debit-order':
          state.form['debit-order'] = data.value
          dirty = true
          break
        case 'stop-order':
          state.form['stop-order'] = data.value
          dirty = true
          break
        case 'mobile-wallet':
          state.form['mobile-wallet'] = data.value
          dirty = true
          break
        case 'payment-start-date':
          const date = dayjs(data.value)
          state.form['payment-start-date'] = date.isValid()
            ? date.format('YYYYMMDD')
            : null
          dirty = true
          break
        case 'payment-frequency':
          Object.keys(state.form['payment-frequency']).forEach((k) => {
            state.form['payment-frequency'][k] = null
          })
          state.form['payment-frequency'][data.value] = 'Y'
          dirty = true
          break
        case 'monthly':
        case 'quarterly':
        case 'yearly':
        case 'annually':
          Object.keys(state.form['payment-frequency']).forEach((k) => {
            state.form['payment-frequency'][k] = null
          })
          // Map 'annually' to 'yearly' in the form data
          const freqKey = data.id === 'annually' ? 'yearly' : data.id
          state.form['payment-frequency'][freqKey] = 'Y'
          dirty = true
          break
      }
      break

    case 'debit-order-details':
      if (data.id in state.form['payment-bank-details']) {
        state.form['payment-bank-details'][data.id] = data.value
        dirty = true
      } else if (data.id === 'interactive-canvas-html') {
        state.form['payer-signature'] = data.value
        dirty = true
      }
      break

    case 'stop-order-details':
      if (data.id in state.form['payment-employment-details']) {
        state.form['payment-employment-details'][data.id] = data.value
        dirty = true
      } else if (data.id === 'interactive-canvas-html') {
        state.form['payer-signature'] = data.value
        dirty = true
      }
      break

    case 'mobile-wallet-details':
      if (data.id in state.form['mobile-wallet-details']) {
        state.form['mobile-wallet-details'][data.id] = data.value
        dirty = true
      } else if (data.id === 'interactive-canvas-html') {
        state.form['payer-signature'] = data.value
        dirty = true
      }
      break

    case 'upload-document-pages':
      const doc = data.id.match(/^upload-(\d+)$/)
      if (doc) {
        if (doc <= state.local.pages.length) {
          state.local.pages[doc - 1] = data.value
        } else {
          state.local.pages.push(data.value)
        }
        dirty = true
      } else {
        console.warn(
          `No form element found for ${data.id} [${data.value}] in step ${state.step}`
        )
      }
      break

    case 'acceptance-screen':
      if (data.id === 'interactive-canvas-html') {
        state.form['acceptance-signature'] = data.value
      }
      break
  }
  if (dirty) {
    setPageItemFromState(state)
  }
}

export default {
  createState,
  processButton,
  processChange,
}
